/**
 * MaterialSelectorPanel — Section 1 of Material-Accessories page.
 *
 * Performance notes
 * ─────────────────
 * • Owns its own debounced server search — never receives the full Material
 *   list. At 100k+ materials that list doesn't exist client-side at all;
 *   the same "type to search, capped result count" pattern already used by
 *   PriceManagement's material Autocomplete.
 * • All search/filter state is LOCAL — typing never re-renders the parent page.
 * • React.memo prevents parent state changes (type selection, accessory edits,
 *   downloads, etc.) from entering this component at all.
 * • List items use native <button> elements instead of MUI ListItemButton.
 *   Each MUI ListItemButton renders ~20 DOM nodes (ButtonBase + ripple + theme
 *   context + styled wrappers). A plain <button> is 1 node — 20× less DOM work
 *   per item.
 * • CSS hover is handled by a single injected <style> block (one-time cost),
 *   not per-item event listeners or inline style overrides.
 * • Autocomplete replaces MUI Select for Dept + Group Code, providing instant
 *   type-to-search with zero extra state or debounce.
 */
import React, {
  useState, useMemo, useCallback, useEffect, useRef,
} from 'react';
import Paper        from '@mui/material/Paper';
import Typography   from '@mui/material/Typography';
import TextField    from '@mui/material/TextField';
import Chip         from '@mui/material/Chip';
import FilterAutocomplete from '../components/common/FilterAutocomplete';
import SearchIcon         from '@mui/icons-material/Search';
import LoadingSpinner from '../components/common/LoadingSpinner';
import * as materialApi from '../api/materialApi';

/* ─── CSS for list items ─────────────────────────────────────────────────────
 * Defined as a constant so React never diffs or re-creates the style text.
 * Pure-CSS :hover means zero JS event listeners per item.
 * ─────────────────────────────────────────────────────────────────────────── */
const LIST_STYLE = `
  .msp-item {
    display: block; width: 100%; text-align: left;
    padding: 6px 10px; cursor: pointer;
    background: transparent; border: none;
    border-bottom: 1px solid #f0f0f0;
    font-family: inherit;
    transition: background 0.12s, box-shadow 0.12s;
  }
  .msp-item:hover { background-color: #F0F7FF; }
  /* ── Selected: light-blue tint + inset left accent stripe ── */
  .msp-item.msp-selected {
    background: #DBEAFE;
    box-shadow: inset 3px 0 0 #1D4ED8;
    border-bottom-color: #BFDBFE;
  }
  .msp-item.msp-selected:hover { background: #BFDBFE; }
  .msp-item:focus-visible { outline: 2px solid #1D4ED8; outline-offset: -2px; }
  .msp-primary   { font-weight: 600; font-size: 0.875rem; line-height: 1.35; color: #212121; }
  .msp-secondary { font-size: 0.78rem; color: #666; line-height: 1.3; }
  .msp-item.msp-selected .msp-primary   { color: #1E3A8A; font-weight: 700; }
  .msp-item.msp-selected .msp-secondary { color: #2563EB; }
`;

const RESULT_LIMIT = 60;

// Stable sentinel objects for "All" options — defined outside the component so they
// are referentially identical on every render (Autocomplete isOptionEqualToValue).
const ALL_DEPT  = Object.freeze({ id: '', label: 'All Departments' });
const ALL_GROUP = Object.freeze({ id: '', label: 'All Groups' });

const MaterialSelectorPanel = React.memo(function MaterialSelectorPanel({
  groups,
  departments,
  selectedMaterial,
  onSelect,
}) {
  // ── Local state — changes never escape to the parent ───────────────────────
  const [search,      setSearch]      = useState('');
  const [deptFilter,  setDeptFilter]  = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);

  const searchTimer = useRef(null);

  // ── Server-side search — debounced, capped at RESULT_LIMIT rows ────────────
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setLoading(true);
      const params = { limit: RESULT_LIMIT };
      if (search.trim()) params.search = search.trim();
      if (groupFilter) params.materialGroupId = groupFilter;
      else if (deptFilter) params.departmentId = deptFilter;

      materialApi.getAll(params)
        .then((res) => setRows(res.data || res || []))
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [search, deptFilter, groupFilter]);

  // ── Group options scoped by selected department — mirrors MaterialsTab ─────
  const filteredGroups = useMemo(() =>
    deptFilter ? groups.filter((g) => g.departmentId === Number(deptFilter)) : groups,
    [groups, deptFilter]
  );

  // ── Autocomplete options ───────────────────────────────────────────────────
  const deptOptions = useMemo(() => [
    ALL_DEPT,
    ...departments.map((d) => ({ id: String(d.id), label: d.name })),
  ], [departments]);

  const groupOptions = useMemo(() => [
    ALL_GROUP,
    ...filteredGroups.map((g) => ({
      id: String(g.id),
      label: deptFilter ? g.groupCode : `${g.groupCode} — ${g.department?.name}`,
    })),
  ], [filteredGroups, deptFilter]);

  const deptValue  = deptOptions.find((o) => o.id === String(deptFilter))   ?? ALL_DEPT;
  const groupValue = groupOptions.find((o) => o.id === String(groupFilter)) ?? ALL_GROUP;

  // ── Stable handlers ────────────────────────────────────────────────────────
  const handleDeptChange = useCallback((_, opt) => {
    setDeptFilter(opt?.id || '');
    setGroupFilter('');
  }, []);

  const handleGroupChange = useCallback((_, opt) => {
    setGroupFilter(opt?.id || '');
    if (opt?.id && !deptFilter) {
      const group = groups.find((g) => String(g.id) === opt.id);
      if (group?.departmentId) setDeptFilter(String(group.departmentId));
    }
  }, [deptFilter, groups]);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  return (
    <>
      {/* Inject once; React reconciles the text node and never touches the
          style element again as long as LIST_STYLE is a module-level constant. */}
      <style>{LIST_STYLE}</style>

      <Paper
        elevation={selectedMaterial ? 2 : 0}
        sx={{
          p: 1.5,
          bgcolor: selectedMaterial ? '#EFF6FF' : '#FAFAFA',
          border: '2px solid',
          borderColor: selectedMaterial ? '#1D4ED8' : '#E0E0E0',
          transition: 'all 0.2s',
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: selectedMaterial ? 0.75 : 1.25, fontWeight: 700, color: '#424242', fontSize: '0.8rem', letterSpacing: 0.3, textTransform: 'uppercase' }}>
          1. Select Material
        </Typography>

        {/* Always-visible indicator of which material is currently selected.
            Remains visible even when the item isn't in the current search results. */}
        {selectedMaterial && (
          <Chip
            label={selectedMaterial.name}
            size="small"
            sx={{
              mb: 1.25,
              maxWidth: '100%',
              bgcolor: '#1D4ED8',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 22,
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            }}
          />
        )}

        {/* Search — native inputProps override forces compact height regardless of MUI size */}
        <TextField
          fullWidth
          size="small"
          placeholder="Type to search materials…"
          value={search}
          onChange={handleSearchChange}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.75, color: '#9CA3AF', fontSize: 16 }} /> }}
          inputProps={{ style: { padding: '5px 8px 5px 0', fontSize: '0.8rem' }, autoComplete: 'off' }}
          sx={{ mb: 1 }}
        />

        {/* Department — FilterAutocomplete: blank on open, placeholder shows selection */}
        <FilterAutocomplete
          label="Department"
          size="small"
          fullWidth
          options={deptOptions}
          value={deptValue}
          onChange={handleDeptChange}
          getOptionLabel={(opt) => opt.label}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          inputProps={{ style: { padding: '5px 8px 5px 0', fontSize: '0.8rem' } }}
          sx={{ mb: 0.75 }}
        />

        {/* Group Code — FilterAutocomplete: blank on open, placeholder shows selection */}
        <FilterAutocomplete
          label="Group Code"
          size="small"
          fullWidth
          options={groupOptions}
          value={groupValue}
          onChange={handleGroupChange}
          getOptionLabel={(opt) => opt.label}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          inputProps={{ style: { padding: '5px 8px 5px 0', fontSize: '0.8rem' } }}
          sx={{ mb: 1.25 }}
        />

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div style={{ border: '1px solid #E0E0E0', borderRadius: 4, overflow: 'hidden' }}>
            {rows.length === 0 ? (
              <Typography sx={{ p: 2, textAlign: 'center', color: '#999' }}>
                No materials found
              </Typography>
            ) : (
              <>
                <div style={{
                  padding: '3px 8px', background: '#EEEEEE', borderBottom: '1px solid #E0E0E0',
                  fontSize: '0.72rem', color: '#555',
                }}>
                  {rows.length}{rows.length === RESULT_LIMIT ? '+' : ''} result{rows.length !== 1 ? 's' : ''} — refine search to narrow further
                </div>

                {/* Native <button> items — no MUI emotion/ripple overhead. *
                  * CSS class above handles hover/selected; React never       *
                  * touches backgroundColor inline so :hover works cleanly.  */}
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {rows.map((mat) => (
                    <button
                      key={mat.id}
                      type="button"
                      className={`msp-item${selectedMaterial?.id === mat.id ? ' msp-selected' : ''}`}
                      onClick={() => onSelect(mat)}
                    >
                      <div className="msp-primary">{mat.name}</div>
                      <div className="msp-secondary">{mat.materialGroup?.groupCode} • {mat.unit}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </Paper>
    </>
  );
});

export default MaterialSelectorPanel;
